import { useState, useEffect } from "react";
import BackMethods from "../../back_component/BackMethods";
import Establishment from "../../models/Establishment";
import img from "../../1.webp";
import { useNavigate } from "react-router-dom";

export default function EstablishmentsPage() {
    const navigate = useNavigate();
    const { backEnd_request } = BackMethods();
    const [establishments, setEstablishments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalEstablishments, setTotalEstablishments] = useState(0);
    const [pageSize] = useState(8);
    const [searchQuery, setSearchQuery] = useState("");
    const [viewMode, setViewMode] = useState('cards');

    const fetchEstablishments = async (page = 1, q = "") => {
        setIsLoading(true);
        try {
            const res = await backEnd_request.get("/Establishments", {
                params: { page, size: pageSize, q },
            });
            if (res.data?.data) {
                const estObjects = res.data.data.map((e) => new Establishment(e));
                setEstablishments(estObjects);
                const total = parseInt(res.data.total) || 0;
                setTotalPages(Math.ceil(total / pageSize));
                setTotalEstablishments(total);
            }
        } catch (error) {
            console.error("Fetch Error:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchEstablishments(currentPage, searchQuery);
    }, [currentPage, searchQuery]);

    const handlePageChange = (pageNumber) => {
        if (pageNumber < 1 || pageNumber > totalPages) return;
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const goToViolations = (e, fieldUID) => {
        e.stopPropagation();
        navigate(`/admin/violations?establishment=${fieldUID}`);
    };

    // --- مكون الترقيم (Pagination Component) ---
    const Pagination = () => {
        if (totalPages <= 1) return null;

        const pages = [];
        // إظهار الصفحات القريبة من الصفحة الحالية فقط لتجنب الازدحام
        for (let i = Math.max(1, currentPage - 2); i <= Math.min(totalPages, currentPage + 2); i++) {
            pages.push(i);
        }

        return (
            <nav className="d-flex justify-content-center mt-5">
                <ul className="pagination custom-pagination shadow-sm p-2 rounded-4 bg-white">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button className="page-link border-0 rounded-3" onClick={() => handlePageChange(1)}>
                            <i className="bi bi-chevron-double-right"></i>
                        </button>
                    </li>
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button className="page-link border-0 rounded-3 mx-1" onClick={() => handlePageChange(currentPage - 1)}>السابق</button>
                    </li>

                    {pages.map(p => (
                        <li key={p} className={`page-item ${currentPage === p ? 'active' : ''}`}>
                            <button className="page-link border-0 rounded-3 mx-1" onClick={() => handlePageChange(p)}>{p}</button>
                        </li>
                    ))}

                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button className="page-link border-0 rounded-3 mx-1" onClick={() => handlePageChange(currentPage + 1)}>التالي</button>
                    </li>
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button className="page-link border-0 rounded-3" onClick={() => handlePageChange(totalPages)}>
                            <i className="bi bi-chevron-double-left"></i>
                        </button>
                    </li>
                </ul>
            </nav>
        );
    };

    const CardView = ({ est }) => (
        <div key={est.establishmentId} className="col-xl-3 col-lg-4 col-md-6 mb-4">
            <div className="elevated-card" onClick={() => navigate(`/admin/establishments/${est.fieldUID}`)}>
                <div className="card-img-container">
                    <img src={est.externalPhoto?.photoUrl || img} alt={est.name} onError={(e) => e.target.src = img} />
                    <div className="floating-badge">{est.facilityType || "منشأة"}</div>
                </div>
                <div className="card-body-content">
                    <h6 className="title-text text-truncate">{est.name}</h6>
                    <p className="location-text"><i className="bi bi-geo-alt-fill text-danger ms-1"></i> {est.governorate}</p>
                    <div className="stats-pills">
                        <span className="pill">أفراد: <b>{est.persons}</b></span>
                        <span className="pill cyan">أنشطة: <b>{est.activities}</b></span>
                        <span
                            className={`pill click-pill ${est.violationReportsCount > 0 ? 'green-pill' : ''}`}
                            onClick={(e) => est.violationReportsCount > 0 && goToViolations(e, est.fieldUID)}
                        >
                            ضبوط: <b>{est.violationReportsCount || 0}</b>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );

    const TableView = ({ establishments }) => (
        <div className="separated-table px-2">
            {establishments.map((est) => (
                <div key={est.establishmentId} className="table-row-card" onClick={() => navigate(`/admin/establishments/${est.fieldUID}`)}>
                    <div className="row align-items-center w-100 m-0 text-center text-md-end">
                        <div className="col-md-4 text-md-end mb-2 mb-md-0">
                            <span className="row-label">المنشأة</span>
                            <div className="fw-bold text-dark d-block text-truncate">{est.name}</div>
                            <small className="text-info">{est.facilityType}</small>
                        </div>
                        <div className="col-md-3 border-start border-end mb-2 mb-md-0">
                            <span className="row-label">الموقع</span>
                            <div className="text-muted small">{est.governorate} / {est.city}</div>
                        </div>
                        <div className="col-md-3 mb-2 mb-md-0">
                            <span className="row-label">الإحصائيات</span>
                            <div className="d-flex justify-content-center gap-2">
                                <span className="mini-badge">👤 {est.persons}</span>
                                <span className="mini-badge bg-info-light">⚡ {est.activities}</span>
                                <span
                                    className={`mini-badge click-pill ${est.violationReportsCount > 0 ? 'bg-success-light text-success border border-success' : ''}`}
                                    onClick={(e) => est.violationReportsCount > 0 && goToViolations(e, est.fieldUID)}
                                >
                                    📝 {est.violationReportsCount || 0}
                                </span>
                            </div>
                        </div>
                        <div className="col-md-2 text-start d-none d-md-block">
                            <button className="btn-go"><i className="bi bi-arrow-left"></i></button>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );

    return (
        <div className="dashboard-bg my-3 py-4 px-4" dir="rtl">
            <div className="container-fluid">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-5 header-container flex-wrap gap-3">
                    <div>
                        <h4 className="fw-bold mb-0">إدارة الفعاليات</h4>
                        <div className="text-muted small mt-1">إجمالي المنشآت: <span className="badge bg-dark rounded-pill">{totalEstablishments}</span></div>
                    </div>
                    <div className="d-flex gap-3 align-items-center">
                        <div className="premium-search">
                            <i className="bi bi-search"></i>
                            <input
                                type="text"
                                placeholder="ابحث عن منشأة..."
                                value={searchQuery}
                                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                            />
                        </div>
                        <div className="switcher-container shadow-sm">
                            <button className={viewMode === 'cards' ? 'active' : ''} onClick={() => setViewMode('cards')}><i className="bi bi-grid-3x3-gap"></i></button>
                            <button className={viewMode === 'table' ? 'active' : ''} onClick={() => setViewMode('table')}><i className="bi bi-list-ul"></i></button>
                        </div>
                    </div>
                </div>

                {isLoading ? (
                    <div className="text-center py-5"><div className="spinner-border text-info"></div></div>
                ) : (
                    <>
                        <div className="row g-3">
                            {viewMode === 'cards' ?
                                establishments.map((est) => <CardView key={est.establishmentId} est={est} />) :
                                <div className="col-12"><TableView establishments={establishments} /></div>
                            }
                        </div>

                        {/* عرض الترقيم أسفل النتائج */}
                        <Pagination />
                    </>
                )}
            </div>

            <style>{`
                .dashboard-bg { background-color: #f0f2f5; min-height: 100vh; font-family: 'Inter', 'Cairo', sans-serif; }
                .premium-search { position: relative; display: flex; align-items: center; }
                .premium-search i { position: absolute; right: 15px; color: #adb5bd; }
                .premium-search input { border: 1px solid #dee2e6; padding: 10px 40px 10px 15px; border-radius: 12px; width: 280px; transition: 0.3s; }
                .switcher-container { background: #fff; border-radius: 12px; padding: 4px; display: flex; border: 1px solid #dee2e6; }
                .switcher-container button { border: none; background: transparent; padding: 6px 16px; border-radius: 10px; color: #6c757d; }
                .switcher-container button.active { background: #0dcaf0; color: #fff; }

                /* Pagination Custom Styles */
                .custom-pagination .page-link { color: #6c757d; background: transparent; padding: 8px 16px; font-weight: 600; transition: 0.2s; }
                .custom-pagination .page-item.active .page-link { background-color: #0dcaf0 !important; color: #fff; box-shadow: 0 4px 10px rgba(13, 202, 240, 0.3); }
                .custom-pagination .page-item.disabled .page-link { color: #dee2e6; }
                .custom-pagination .page-link:hover:not(.active) { background-color: #f8f9fa; color: #0dcaf0; }

                .elevated-card { background: #fff; border-radius: 18px; overflow: hidden; transition: 0.3s; cursor: pointer; border: 1px solid #e9ecef; height: 100%; }
                .elevated-card:hover { transform: translateY(-8px); box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1); border-color: #0dcaf0; }
                .card-img-container { height: 110px; position: relative; }
                .card-img-container img { width: 100%; height: 100%; object-fit: cover; }
                .floating-badge { position: absolute; top: 10px; right: 10px; background: rgba(0,0,0,0.7); color: #fff; font-size: 0.65rem; padding: 4px 10px; border-radius: 8px; font-weight: 600; }
                .card-body-content { padding: 15px; }
                .title-text { font-weight: 700; color: #212529; font-size: 0.9rem; margin-bottom: 4px; }
                .location-text { font-size: 0.75rem; color: #6c757d; margin-bottom: 12px; }
                .stats-pills { display: flex; gap: 5px; flex-wrap: wrap; }
                .pill { background: #f8f9fa; border: 1px solid #e9ecef; padding: 2px 7px; border-radius: 8px; font-size: 0.65rem; color: #495057; }
                .pill.cyan { border-color: #0dcaf0; color: #0dcaf0; background: rgba(13, 202, 240, 0.02); }
                .click-pill { cursor: pointer; transition: 0.2s; }
                .click-pill:hover { transform: scale(1.05); filter: brightness(0.9); }
                .green-pill { border-color: #198754 !important; color: #198754 !important; background: rgba(25, 135, 84, 0.05) !important; font-weight: bold; }
                .table-row-card { background: #fff; border-radius: 16px; padding: 15px 20px; margin-bottom: 12px; display: flex; align-items: center; transition: 0.3s; border: 1px solid #e9ecef; cursor: pointer; }
                .table-row-card:hover { transform: translateX(5px); border-color: #0dcaf0; }
                .row-label { display: block; font-size: 0.65rem; color: #adb5bd; font-weight: 700; margin-bottom: 2px; }
                .mini-badge { background: #f1f3f5; padding: 4px 10px; border-radius: 8px; font-size: 0.75rem; font-weight: 600; transition: 0.2s; }
                .bg-info-light { background: rgba(13, 202, 240, 0.1); color: #087990; }
                .bg-success-light { background: rgba(25, 135, 84, 0.1); }
                .btn-go { width: 32px; height: 32px; border-radius: 8px; border: none; background: #f8f9fa; color: #adb5bd; }
            `}</style>
        </div>
    );
}